import type { ErrorEvent } from '@sentry/react'

/**
 * Errors Sentry drops by message: browser and environment noise that doesn't represent a Tunasong bug.
 *
 * Never a Slate "Cannot resolve a … node" message (GH #2686): that is how a real bug looks, such as a block that drops
 * Slate's element ref (GH #2662), and a blanket ignore hid that one for months. slate-react's own teardown race is
 * filtered by its stack instead, in `dropKnownNoise`.
 */
export const sentryIgnoreErrors: (string | RegExp)[] = [
  // AbortError fires whenever a fetch/AbortController is intentionally cancelled
  // (route change, cleanup). NotReadableError surfaces from OS-level I/O failures
  // on file/storage access. The IndexedDB transaction error happens when a request
  // outlives its transaction — typical during browser-tab unmount.
  /AbortError/,
  /NotReadableError/,
  /Attempt to get records from database without an in-progress transaction/,
  // Stale chunk after a deploy: the user's tab still references hashed bundle
  // names that have since been replaced. Resolves on next page load.
  /Importing a module script failed/,
  /Failed to fetch dynamically imported module/,
  // Unhandled RTK Query rejection — the auth flow can race a 401 against
  // our session refresh. Sentry surfaces these as "Object captured as
  // promise rejection with keys: error, meta" with no stack frame.
  // Tracked separately as an opportunity to add a global try/catch on
  // unwrap() callers, but the rejection itself is recoverable.
  /Object captured as promise rejection with keys: error, meta/,
  // An email link scanner's injected script, rejecting when its host has gone away (GH #2895, TUNASONG-X8).
  // Tunasong has no MethodName/ParamCount RPC.
  /Object Not Found Matching Id:\d+, MethodName:update, ParamCount:\d+/,
]

const slateResolveError = /Cannot resolve a (DOM|Slate) node from (Slate|DOM) node/

/** lodash's debounce/throttle, which slate-react wraps its selection-change callback in */
const isTimerFrame = (filename: string | undefined) =>
  Boolean(filename && /[\\/]lodash[\\/](debounce|throttle)/.test(filename))

/**
 * slate-react's known teardown race (GH #2686): its throttled `onDOMSelectionChange` fires from a lodash timer after
 * the editor unmounted, and throws "Cannot resolve a DOM/Slate node". No user impact, and nothing Tunasong can fix
 * short of patching slate-react.
 *
 * Only that is filtered, recognised by the timer frame in its stack. The same message from an event handler is a
 * real bug, such as a block that drops Slate's element ref (GH #2662), and is reported.
 */
export const isSlateSelectionTeardownRace = (event: ErrorEvent) =>
  (event.exception?.values ?? []).some(
    exception =>
      slateResolveError.test(exception.value ?? '') &&
      (exception.stacktrace?.frames ?? []).some(frame => isTimerFrame(frame.filename))
  )

/**
 * Sentry `beforeSend`: drop known noise that a message alone can't tell from a bug.
 *
 * Message-level ignores for Slate's "Cannot resolve a Slate node from DOM node" / "Cannot resolve a DOM node from Slate
 * node" are banned, in `ignoreErrors`, `denyUrls` or anywhere else (the user's standing rule, 2026-10-24). The same
 * message is how a real editor bug looks: GH #2662, a chord block that dropped Slate's element ref, threw it on every
 * chord click from 2026-04 until a user reported it, because a blanket ignore hid it from Sentry (GH #2686). Filter
 * only by stack, as `isSlateSelectionTeardownRace` does. sentry-slate-errors.test.tsx checks what `Sentry.init`
 * receives, and fails if a message-level filter for these errors comes back.
 */
export const dropKnownNoise = (event: ErrorEvent): ErrorEvent | null =>
  isSlateSelectionTeardownRace(event) ? null : event
